import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronLeft, CircleAlert, ListChecks as ListChecksIcon, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getUserSurvey } from "../hooks/fetching/getUserSurvey";
import { apiClient } from "../hooks/clientApi";
import type { SurveyDetailUser } from "../hooks/fetching/getUserSurvey";
import { useToast } from "../context/ToasterContext";
import { ButtonComponent } from "../components/ui/buttonComponent";
import Drawer from "../components/Drawer";
import { useSubmitSurvey } from "../hooks/mutations/post-survey-result";

const TakeSurvey = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { showToast } = useToast();

  // ===== FETCH DATA =====
  const { data: surveyDetails, isFetching: isFetchingSurvey } = useQuery<SurveyDetailUser>({
    queryKey: ["surveyDetails", searchParams.get("survey_id")],
    queryFn: () => getUserSurvey(Number(searchParams.get("survey_id")) || 0),
    retry: 2,
    staleTime: Infinity,
    enabled: !!searchParams.get("survey_id"),
  });

  // ===== SURVEY STATE =====
  const [responses, setResponses] = useState<any>(null);
  const [errors, setErrors] = useState<{ mcqs: string[]; oeqs: string[] }>({ mcqs: [], oeqs: [] });
  const [startTime, setStartTime] = useState<number | null>(null);
  const [duration, setDuration] = useState<number>(0);
  const [submitted, setSubmitted] = useState<boolean>(false);

  // Start timer once survey loads
  useEffect(() => {
    if (surveyDetails && !startTime) {
      setStartTime(Date.now());
    }
  }, [surveyDetails]);

  const submitSurvey = useSubmitSurvey({
    onSuccess: () => {
      showToast({ header: "Survey submitted successfully!", error: false });
      setIsOpen(false);
      navigate("/home");
    },
    onFail: () => {
      showToast({ header: "Failed to submit survey.", error: true });
    },
  });

  // Timer tick
  useEffect(() => {
    if (!startTime) return;
    const interval = setInterval(() => {
      setDuration(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  // Initialize response structure
  useEffect(() => {
    if (surveyDetails?.surveyJson && surveyDetails.surveyJson.length > 0) {
      const parsed = surveyDetails.surveyJson;
      setResponses({
        mcqs: parsed
          .filter((q: any) => q.type === "single-choice" || q.type === "multi-choice")
          .map((q: any) => ({
            ...q,
            selectedAnswerIndex: q.type === "single-choice" ? null : [],
          })),
        oeqs: parsed.filter((q: any) => q.type === "open-ended").map((q: any) => ({ ...q, answer: "" })),
      });
    }
  }, [surveyDetails]);

  // ===== HANDLERS =====
  const handleSelectAnswer = (qIndex: number, aIndex: number, type: string) => {
    setResponses((prev: any) => {
      const updated = { ...prev };
      if (type === "single-choice") {
        updated.mcqs[qIndex].selectedAnswerIndex = aIndex;
      } else if (type === "multi-choice") {
        const selected = updated.mcqs[qIndex].selectedAnswerIndex as number[];
        if (selected.includes(aIndex)) {
          updated.mcqs[qIndex].selectedAnswerIndex = selected.filter((i: number) => i !== aIndex);
        } else {
          updated.mcqs[qIndex].selectedAnswerIndex = [...selected, aIndex];
        }
      }
      return { ...updated };
    });
  };

  const handleOpenAnswer = (qIndex: number, value: string) => {
    setResponses((prev: any) => {
      const updated = { ...prev };
      updated.oeqs[qIndex].answer = value;
      return { ...updated };
    });
  };

  // Validation
  const validateSurvey = () => {
    if (!responses) return false;

    const mcqErrors: string[] = [];
    const oeqErrors: string[] = [];

    responses.mcqs.forEach((q: any) => {
      if (q.type === "single-choice" && q.selectedAnswerIndex === null) {
        mcqErrors.push("Please select an option.");
      } else if (q.type === "multi-choice" && (!q.selectedAnswerIndex || q.selectedAnswerIndex.length === 0)) {
        mcqErrors.push("Please select at least one option.");
      } else {
        mcqErrors.push("");
      }
    });

    responses.oeqs.forEach((q: any) => {
      if (!q.answer || q.answer.trim() === "") {
        oeqErrors.push("This question requires an answer.");
      } else if (q?.settings?.minChars && q.answer.length < q.settings.minChars) {
        oeqErrors.push(`Minimum ${q.settings.minChars} characters required.`);
      } else if (q?.settings?.maxChars && q.answer.length > q.settings.maxChars) {
        oeqErrors.push(`Maximum ${q.settings.minChars} characters allowed.`);
      } else {
        oeqErrors.push("");
      }
    });

    setErrors({ mcqs: mcqErrors, oeqs: oeqErrors });

    return [...mcqErrors, ...oeqErrors].every((msg) => msg === "");
  };

  const handleSubmit = async () => {
    if (!responses || !surveyDetails) return;

    setSubmitted(true);

    const isValid = validateSurvey();
    if (!isValid) {
      showToast({ error: true, header: "Please fix the errors before submitting." });
      return;
    }

    const payload = {
      survey_id: surveyDetails.id,
      duration, // total seconds
      survey_result: [
        ...responses.mcqs.map((q: any) => {
          let selectedAnswerIds: string[] = [];

          if (q.type === "single-choice" && q.selectedAnswerIndex !== null) {
            selectedAnswerIds = [q.options[q.selectedAnswerIndex].id];
          } else if (q.type === "multi-choice" && Array.isArray(q.selectedAnswerIndex)) {
            selectedAnswerIds = q.selectedAnswerIndex.map((idx: number) => q.options[idx].id);
          }

          return {
            id: q.id,
            question: q.text,
            answers: q.options.map((o: any) => o.answer),
            selectedAnswerIds,
            answerDuration: 1, // refine if you track per-question duration
          };
        }),
        ...responses.oeqs.map((q: any) => ({
          id: q.id,
          question: q.text,
          answer: q.answer,
          answerDuration: 1,
        })),
      ],
    };

    submitSurvey.mutate(payload);
  };

  // ===== DERIVED =====
  const sessionDuration = useMemo(() => {
    const m = Math.floor(duration / 60);
    const s = duration % 60;
    return `${m}m ${s}s`;
  }, [duration]);

  return (
    <>
      <Drawer isOpen={isOpen} setIsOpen={setIsOpen} header="" className="!h-fit !min-h-auto">
        <div className="flex flex-col items-center gap-4">
          <div className="h-16 w-16 rounded-full flex items-center justify-center bg-[#FEF3C7] text-xuron-orange">
            <CircleAlert className="w-8 h-8" />
          </div>
          <div className="flex flex-col  gap-2">
            <h1 className="text-2xl font-bold text-center">Ready to Submit?</h1>
            <p className="text-base font-normal text-gray-500 text-center">
              Once you submit your survey, you won't be able to make changes. Are you sure you want to continue?
            </p>
          </div>
          <div className="flex items-center justify-center gap-3">
            <ButtonComponent
              className="!text-gray-500 flex items-center gap-1 !text-base !border-gray-200 border !bg-white"
              onClick={() => setIsOpen(false)}
            >
              <ArrowLeft className="w-5 h-5" /> Go Back
            </ButtonComponent>
            <ButtonComponent className="flex items-center gap-1 !text-base" onClick={() => handleSubmit()}>
              <Check className="w-5 h-5" /> Submit Survey
            </ButtonComponent>
          </div>
          <div className="h-4"></div>
        </div>
      </Drawer>
      <div className="w-full h-full flex flex-col gap-4 md:gap-6 p-4 md:p-6">
        {/* Back */}
        <div className="flex items-center justify-between no-print flex-wrap gap-2">
          <Link
            to={searchParams.get("backto") || "/users"}
            className="flex items-center gap-2 text-gray-600 hover:text-orange-500 transition-colors font-medium text-sm md:text-base"
          >
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
            {t("users.back")}
          </Link>
        </div>

        {/* Survey Block */}
        {isFetchingSurvey ? (
          <p>Loading survey...</p>
        ) : responses ? (
          <div className="h-fit bg-white rounded-2xl md:rounded-3xl overflow-hidden border border-gray-100 shadow-sm">
            {/* Header */}
            <div className="bg-xuron-orange p-4 md:p-8 flex flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center">
              <div className="w-10 h-10 md:w-12 md:h-12 shrink-0 bg-white rounded-full flex justify-center items-center">
                <ListChecksIcon className="w-5 h-5 md:w-6 md:h-6 text-orange-500" />
              </div>
              <div className="flex flex-col gap-1">
                <h1 className="text-white text-lg md:text-2xl font-bold">Follow Up Survey</h1>
                <h1 className="text-white text-xs md:text-sm font-normal">
                  Confidence, feedback and knowledge assessment
                </h1>
              </div>
              <p className="ml-auto text-white text-sm">Time spent: {sessionDuration}</p>
            </div>

            {/* MCQs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 p-4 md:p-6">
              {responses.mcqs.map((q: any, qIndex: number) => (
                <div key={q.id} className="bg-white rounded-2xl p-4 md:p-6 shadow-sm">
                  <div className="flex flex-col items-start gap-1 mb-3 md:mb-4">
                    <div className="flex justify-between w-full items-center">
                      <div className="bg-orange-500 text-white rounded-full w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-xs md:text-sm font-bold">
                        {qIndex + 1}
                      </div>
                      <div className="rounded-full px-3 py-1 md:px-4 md:py-2 text-white bg-orange-500 text-xs md:text-sm">
                        {q.type === "multi-choice" ? "MULTIPLE SELECT" : "MULTIPLE CHOICE"}
                      </div>
                    </div>
                    <p className="text-gray-900 font-medium leading-relaxed text-sm md:text-base">{q.text}</p>
                  </div>
                  <div className="space-y-2 md:space-y-3">
                    {q.options?.map((opt: any, aIndex: number) => {
                      const isSelected =
                        q.type === "single-choice"
                          ? q.selectedAnswerIndex === aIndex
                          : (q.selectedAnswerIndex as number[]).includes(aIndex);

                      return (
                        <div
                          key={opt.id}
                          onClick={() => handleSelectAnswer(qIndex, aIndex, q.type)}
                          className={`cursor-pointer flex items-center gap-3 p-3 md:p-4 rounded-2xl border-2 transition-all 
        ${isSelected ? "border-orange-500 bg-orange-50" : "border-gray-300 bg-white hover:border-orange-300"}`}
                        >
                          <div
                            className={`w-5 h-5 flex items-center justify-center rounded-full border-2
          ${isSelected ? "border-orange-500 bg-orange-500" : "border-gray-300 bg-white"}`}
                          >
                            {isSelected && q.type === "single-choice" && (
                              <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                            )}
                            {isSelected && q.type === "multi-choice" && <Check className="text-white" />}
                          </div>
                          <span
                            className={`text-sm md:text-base font-medium ${
                              isSelected ? "text-orange-600" : "text-gray-700"
                            }`}
                          >
                            {opt.answer}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {submitted && errors.mcqs[qIndex] && (
                    <p className="text-red-500 text-sm mt-2">{errors.mcqs[qIndex]}</p>
                  )}
                </div>
              ))}
            </div>

            {/* OEQs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 p-4 md:p-6">
              {responses.oeqs.map((q: any, qIndex: number) => (
                <div key={q.id} className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
                  <div className="flex flex-col items-start gap-1 mb-3 md:mb-4">
                    <div className="flex justify-between w-full items-center">
                      <div className="bg-blue-500 text-white rounded-full w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-xs md:text-sm font-bold">
                        {qIndex + 1}
                      </div>
                      <div className="rounded-full px-3 py-1 md:px-4 md:py-2 text-white bg-blue-500 text-xs md:text-sm">
                        OPEN ENDED
                      </div>
                    </div>
                    <p className="text-gray-900 font-medium leading-relaxed text-sm md:text-base">{q.text}</p>
                  </div>
                  <textarea
                    className="w-full border text-base border-gray-200 min-h-[60px] rounded-lg p-2"
                    value={q.answer}
                    minLength={q?.settings?.minChars}
                    maxLength={q?.settings?.maxChars}
                    onChange={(e) => handleOpenAnswer(qIndex, e.target.value)}
                    placeholder="Type your answer..."
                  />
                  {submitted && errors.oeqs[qIndex] && (
                    <p className="text-red-500 text-sm mt-2">{errors.oeqs[qIndex]}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p>No survey found.</p>
        )}

        <ButtonComponent className="flex items-center gap-1 self-end !w-fit" onClick={() => setIsOpen(true)}>
          <Send className="w-5 h-5" /> Submit Survey
        </ButtonComponent>
      </div>
    </>
  );
};

export default TakeSurvey;
